/**
 * タグ名の正規化と類似判定（FKV3-13002）
 *
 * 表記揺れの防止は3層で行う。
 *  1. DB の UNIQUE 制約（normalized_name）… ここで作る strictKey と同じ結果になる
 *  2. 似た名前の警告（このファイル）… ハード制約にすると意味の違う値まで潰すため警告に留める
 *  3. 店舗へタグを付ける操作に自由入力欄を置かない
 *
 * strictKey はバックエンド（models/seller_tag.rs の normalization_key）と同じ結果になる
 * 必要がある。ずれると「画面では作成できたのに 409 が返る」という挙動になるため。
 */

/**
 * 画面に表示する名称へ整える。
 * NFKC で全角英数・半角カナ・全角スペースの差を吸収し、前後の空白を落として
 * 連続する空白を1つに畳む。語中の1スペースは意味を持ちうるので残す。
 */
export function normalizeDisplayName(raw: string): string {
  return raw
    .normalize('NFKC')
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .join(' ');
}

/**
 * 重複判定に使うキー。バックエンドの normalization_key と同じ結果になる。
 * 「イオンモール 幕張」と「イオンモール幕張」、「EC」と「ec」を同じ値とみなす。
 */
export function strictKey(raw: string): string {
  return normalizeDisplayName(raw).replace(/\s+/g, '').toLowerCase();
}

/**
 * 警告用の緩いキー。strictKey からさらにカタカナをひらがなへ寄せ、
 * 長音・中黒・各種ハイフン・括弧を落とす。
 *
 * これを一意制約に使うと「ロング」と「ロンク」のような別物まで潰しかねないので、
 * あくまで「ほぼ同じです」と伝えるためだけに使う。
 */
export function looseKey(raw: string): string {
  return strictKey(raw)
    .replace(/[ァ-ヶ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))
    .replace(/[ー‐‑–—−\-・･()（）[\]「」【】]/g, '');
}

/** レーベンシュタイン距離 */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(
        prev[j] + 1,
        current[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = current;
  }
  return prev[b.length];
}

/** 名称チェックの結果 */
export type TagNameCheck =
  | { kind: 'ok' }
  | { kind: 'empty' }
  | { kind: 'too_long'; length: number }
  /** 既存と同一。作成させない */
  | { kind: 'duplicate'; existing: string }
  /** ほぼ同じ（カナ違い・記号違い）。作成はできるが強く警告する */
  | { kind: 'near_duplicate'; existing: string }
  /** 似ている（編集距離1・部分一致）。弱く警告する */
  | { kind: 'similar'; existing: string };

/** 名称の最大文字数。バックエンドの MAX_TAG_NAME_CHARS と揃える */
export const MAX_TAG_NAME_CHARS = 100;

/** 編集距離で似ていると判定する最小の長さ。短い語は1文字違いでも別物のことが多い */
const MIN_LENGTH_FOR_EDIT_DISTANCE = 3;

/**
 * 入力された名称を既存の名称群と突き合わせる。
 *
 * 照合対象は同じタグ種別の中だけに限ること（タグ種別が違えば同名は正当なため）。
 */
export function checkTagName(raw: string, existingNames: string[]): TagNameCheck {
  const display = normalizeDisplayName(raw);
  const key = strictKey(raw);
  if (key.length === 0) return { kind: 'empty' };
  if (display.length > MAX_TAG_NAME_CHARS) {
    return { kind: 'too_long', length: display.length };
  }

  const duplicate = existingNames.find((name) => strictKey(name) === key);
  if (duplicate) return { kind: 'duplicate', existing: duplicate };

  const loose = looseKey(raw);
  const nearDuplicate = existingNames.find((name) => looseKey(name) === loose);
  if (nearDuplicate) return { kind: 'near_duplicate', existing: nearDuplicate };

  const similar = existingNames.find((name) => {
    const otherKey = strictKey(name);
    if (
      key.length >= MIN_LENGTH_FOR_EDIT_DISTANCE &&
      otherKey.length >= MIN_LENGTH_FOR_EDIT_DISTANCE &&
      editDistance(key, otherKey) === 1
    ) {
      return true;
    }
    // 片方が他方を丸ごと含む場合も、付け足しの取り違えとして警告する
    return (
      key.length >= MIN_LENGTH_FOR_EDIT_DISTANCE &&
      otherKey.length >= MIN_LENGTH_FOR_EDIT_DISTANCE &&
      (key.includes(otherKey) || otherKey.includes(key))
    );
  });
  if (similar) return { kind: 'similar', existing: similar };

  return { kind: 'ok' };
}

/** チェック結果を画面に出す文言にする */
export function tagNameCheckMessage(check: TagNameCheck, name: string): string | null {
  switch (check.kind) {
    case 'ok':
      return null;
    case 'empty':
      return 'タグ名を入力してください';
    case 'too_long':
      return `タグ名は${MAX_TAG_NAME_CHARS}文字以内で入力してください（${check.length}文字）`;
    case 'duplicate':
      return `「${check.existing}」がすでにあります。既存のタグ名を使ってください`;
    case 'near_duplicate':
      return `「${name}」は「${check.existing}」とほぼ同じです。別のタグとして作りますか？`;
    case 'similar':
      return `「${name}」は「${check.existing}」に似ています`;
  }
}

/**
 * 複数行のテキストをタグ名の配列にする。
 * 空行は捨て、正規化キーが同じものは先に書かれたものを残す。
 */
export function parseTagNameLines(text: string): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const line of text.split('\n')) {
    const display = normalizeDisplayName(line);
    const key = strictKey(line);
    if (key.length === 0) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(display);
  }
  return names;
}
